import { useState } from 'react';
import classNames from 'classnames';

import Alert from '@preqin/ui-components/cjs/core/Alerts/Alert';

import { Workspace } from '@/api/types';

import { checkIsTrialWorkspace, WorkspaceAppPath } from '../../constants/workspaceNames';
import { useAlertMessage } from '../../hooks/useAlertMessage';
import { useWorkspaceVisualTitles } from '../../hooks/useWorkspaceVisualTitles';
import { ToastAlert } from '../alert/ToastAlert';
import { TransactionIntelligenceRedirectBanner } from '../alert/TransactionIntelligenceRedirectBanner';
import { SideNav } from '../SideNav';

import { PowerBIReportFilterPanel } from './filter/PowerBIReportFilterPanel';
import { PowerBiReport } from './PowerBiReport';

import styles from './PowerBiWorkspace.module.scss';

export const PowerBiWorkspace = ({ workspace }: { workspace: Workspace }) => {
  const [visibleReport, setVisibleReport] = useState(workspace.reports.at(0));
  const [showTRINRedirectBanner, setShowTRINRedirectBanner] = useState(
    workspace?.appPath === WorkspaceAppPath.TransactionIntelligence,
  );
  const { alertMessage, setAlertMessage } = useAlertMessage();
  const visualTitles = useWorkspaceVisualTitles(workspace.appPath, workspace);
  const isTrialWorkspace = checkIsTrialWorkspace(workspace.appPath);

  return (
    <>
      <div className={styles.trialAlert}>
        {isTrialWorkspace && (
          <Alert
            showNotification
            message="You are currently using a trial version. The data displayed is mock data for demonstration purposes only."
            type="feedback"
            closeEnabled={false}
            priority="warning"
            size="medium"
          />
        )}
      </div>
      <div className={styles.workspace}>
        {workspace?.isMultipleReportsEnabled && (
          <SideNav visible={workspace.reports.length > 1}>
            {workspace.reports.map((report) => {
              return (
                <div
                  key={report.id}
                  id={report.id}
                  className={classNames(styles.reportLink, {
                    [styles.reportLink__active]: report.id === visibleReport.id,
                  })}
                  onClick={() => setVisibleReport(report)}
                >
                  {report.name}
                </div>
              );
            })}
          </SideNav>
        )}

        <div className={styles.reportPanel}>
          {showTRINRedirectBanner && (
            <div className={styles.redirectWarningPanel}>
              <TransactionIntelligenceRedirectBanner
                handleOnClose={() => setShowTRINRedirectBanner(!showTRINRedirectBanner)}
              />
            </div>
          )}
          {(workspace?.isFilterEnabled || workspace?.isFileExportEnabled) && (
            <PowerBIReportFilterPanel
              visualTitles={visualTitles}
              report={visibleReport}
              isFilterEnabled={workspace?.isFilterEnabled}
              isFileExportEnabled={workspace?.isFileExportEnabled}
              setAlertMessage={setAlertMessage}
            />
          )}
          <PowerBiReport
            report={visibleReport}
            workspaceName={workspace.workspaceName}
            accessToken={workspace.accessToken}
            workspacePath={workspace.appPath}
          />
          <ToastAlert
            showNotification={alertMessage.showNotification}
            priority={alertMessage.priority}
            message={alertMessage.message}
          />
        </div>
      </div>
    </>
  );
};
